import {
  Component, AfterViewInit, Input,
  SimpleChanges, OnChanges, Output, EventEmitter
} from '@angular/core';
import { MapBounds } from './models/map.bounds';
import { MapLatLng } from './models/map.latlng';
import { MapMarker } from './models/map.marker';
import { MapCluster } from './models/map.cluster';
import { MapPolyline } from './models/map.polyline';
import { MapPolylinePoint } from './models/map.polylinePoint';
import { MapCircle } from './models/map.circle';
import { Subscription } from 'rxjs';
import { MapPolygon } from './models/map.polygon';
import { MapPolygonPoint } from './models/map.polygonPoint';
import { MapService } from './map.service';
import { HeatMap } from './models/map.heatMap';
import { MapClusterV2 } from './movisat/map-cluster-v2';
import { MapSearch } from './models/map.search';
import { MapPoint } from './models/map.point';
import { MapItinerary } from './models/map.itinerary';
import { OverlapMarker } from './movisat/overlap-markers';

@Component({
  selector: 'movisat-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.css']
})
export class MapComponent implements AfterViewInit, OnChanges {
  public map: any;
  private overlapMarkers: OverlapMarker;

  // Marcadores
  private markerId = 1; // Id secuencial de marcadores
  protected markersList = new Map<number, MapMarker>();

  // Clusters
  private clusterId = 1; // Id secuencial de cluster
  protected clusterList = new Map<number, MapCluster>();

  // Clusters V2
  private clusterIdV2 = 1; // Id secuencial de cluster
  protected clusterListV2 = new Map<number, MapClusterV2>();

  // Polilineas
  private polylineId = 1; // Id secuencial de polilineas
  protected polylineList = new Map<number, MapPolyline>();
  private polylinePointId = 1; // Id secuencial de puntos de polilineas

  // Polígonos
  private polygonId = 1; // Id secuencial de polígonos
  protected polygonList = new Map<number, MapPolygon>();
  private polygonPointId = 1; // Id secuencial de puntos de polígonos

  // Círculos
  private circleId = 1; // Id secuencial de círculos
  protected circleList = new Map<number, MapCircle>();

  // Mapas de calor
  private heatMapId = 1; // Id secuencial de mapa de calor
  protected heatMapList = new Map<number, HeatMap>();

  // Inputs del mapa
  @Input()
  zoom = 6; // Valor por defecto
  @Input()
  center = { // Valor por defecto, Cieza
    lat: 38.2378331,
    lng: -1.42102,
    weight: 1
  };
  @Input()
  provider = 'Google'; // Proveedor por defecto
  @Input()
  cartoType = 'raster'; // Indica si la cartografía es raster o vectorial
  @Input()
  lang = 'es-ES'; // Idioma por defecto
  @Input()
  searchCountry = 'es'; // Pais por defecto a efectos de búsquedas
  @Input()
  texts = new Map<string, string>(); // Textos del idioma cargado
  @Input()
  searchBounds = ''; // Marco geográfico a efectos de búsquedas
  @Input()
  noSearch = false; // Permite indicar que no queremos buscador

  // Outputs del mapa
  @Output()
  mapReady = new EventEmitter<MapComponent>();
  @Output()
  mapClick = new EventEmitter<MapLatLng>();
  @Output()
  mapMouseMove = new EventEmitter<MapLatLng>();
  @Output()
  boundsChange = new EventEmitter<MapBounds>();
  @Output()
  zoomChange = new EventEmitter<number>();
  @Output()
  markerClick = new EventEmitter<MapMarker>();
  @Output()
  markerClickGeoJson = new EventEmitter<MapMarker>();
  @Output()
  markerDragEnd = new EventEmitter<MapMarker>();
  @Output()
  markerMove = new EventEmitter<MapMarker>();
  @Output()
  polylineClick = new EventEmitter<MapPolylinePoint>();
  @Output()
  circleClick = new EventEmitter<MapCircle>();
  @Output()
  circleDragEnd = new EventEmitter<MapCircle>();
  @Output()
  circleRadiusChanged = new EventEmitter<MapCircle>();
  @Output()
  polygonClick = new EventEmitter<MapPolygonPoint>();
  @Output()
  polygonDragEnd = new EventEmitter<MapPolygonPoint>();
  @Output()
  mapSearch = new EventEmitter<MapSearch>();

  // Para el componente de búsqueda independiente
  public static provider = '';
  public static cartoType = '';
  public static lang = '';
  public static searchCountry = '';
  public static texts = new Map<string, string>();
  public static searchBounds = '';

  constructor(private mapService: MapService) {
    this.provider = this.provider;
    this.overlapMarkers = new OverlapMarker(this, false);
  }

  ngAfterViewInit() {
    // Esto es para ver como se recibe la app desde el marco
    // setTimeout(() => {
    //   const map: MapComponent = this.mapService.config?.app.getMap();
    //   map.setZoom(5);
    // },1000);
  }

  // Detecta cambios en los @inputs del mapa
  ngOnChanges(changes: SimpleChanges) {
    // Estas variables globales son para el componente de búsqueda independiente
    if (MapComponent.provider !== this.provider) {
      MapComponent.provider = this.provider;
      MapComponent.cartoType = this.cartoType;
      MapComponent.lang = this.lang;
      MapComponent.searchCountry = this.searchCountry;
      MapComponent.texts = this.texts;
      MapComponent.searchBounds = this.searchBounds;
    }
    if (changes['mapProvider'] !== undefined && changes['mapProvider'].currentValue !== changes['mapProvider'].previousValue) {
      this.markersList.clear();
      this.polylineList.clear();
      this.polygonList.clear();
      this.circleList.clear();
    }
    if (this.map !== undefined) {
      if (changes['zoom'] !== undefined && changes['zoom'].currentValue !== changes['zoom'].previousValue) {
        this.map.setZoom(changes['zoom'].currentValue);
      }
      if (changes['center'] !== undefined && changes['center'].currentValue !== changes['center'].previousValue) {
        this.map.setCenter(new MapLatLng(changes['center'].currentValue.lat, changes['center'].currentValue.lng));
      }
    }
  }

  // Genera evento cuando el mapa está cargado
  onMapReady(map: any) {
    this.map = map;
    this.setCenter(this.center);
    this.setZoom(this.zoom);

    // Para cuando se redimensione la ventana
    window.addEventListener("resize", (e: any) => {
      this.map.resize();
    });

    this.mapReady.emit(this);
  }

  // Genera evento cuando se pincha sobre el mapa
  onMapClick(pos: MapLatLng) {
    this.overlapMarkers.restoreMarkersPos();
    this.mapClick.emit(pos);
  }

  // Genera evento cuando se mueve el cursor sobre el mapa
  onMapMouseMove(pos: MapLatLng) {
    this.mapMouseMove.emit(pos);
  }

  // Genera evento cuando cambia el encuadre
  onBoundsChange(bounds: MapBounds) {
    this.center.lat = bounds.swCorner.lat + (bounds.neCorner.lat - bounds.swCorner.lat) / 2;
    this.center.lng = bounds.swCorner.lng + (bounds.neCorner.lng - bounds.swCorner.lng) / 2;
    this.overlapMarkers.restoreMarkersPos();
    this.boundsChange.emit(bounds);
  }

  // Genera evento cuando cambia el nivel de zoom
  onZoomChange(zoom: number) {
    this.zoom = zoom;
    this.overlapMarkers.restoreMarkersPos();
    this.zoomChange.emit(zoom);
  }

  // Cuando se pincha sobre un marcador
  onMarkerClick(markerId: number) {
    // Obtengo el marcador de la lista
    const marker = this.markersList.get(markerId);
    if (marker) {
      this.overlapMarkers.setMarker(marker);
      this.markerClick.emit(marker);
    } else {
      this.overlapMarkers.restoreMarkersPos();
    }
  }

  // Cuando se pincha sobre un marcador de un GeoJson
  onMarkerClickGeoJson(marker: MapMarker) {
    this.markerClick.emit(marker);
  }

  // Cuando se arrastra y deja caer un marcador
  onMarkerDragEnd(markerInfo: any) {
    // Obtengo el marcador de la lista
    const marker = this.markersList.get(markerInfo.id);
    if (marker) {
      // Asigno las nuevas coordenadas al marcador
      marker.position = markerInfo.position;
      this.overlapMarkers.updateMarker(marker);
      this.overlapMarkers.restoreMarkersPos();
      this.markerDragEnd.emit(marker);
    }
  }

  // Cuando se mueve un marcador
  onMarkerMove(markerInfo: any) {
    // Obtengo el marcador de la lista
    const marker = this.markersList.get(markerInfo.id);
    if (marker) {
      // Asigno las nuevas coordenadas al marcador
      marker.position = markerInfo.position;
      this.markerMove.emit(marker);
    }
  }

  // Cuando se pincha sobre una polilinea
  onPolylineClick(point: MapPolylinePoint) {
    this.polylineClick.emit(point);
  }

  // Cuando se pincha sobre un polígono
  onPolygonClick(point: MapPolygonPoint) {
    this.polygonClick.emit(point);
  }

  // Cuando se arrastra un punto de un polígono
  onPolygonDragEnd(point: MapPolygonPoint) {
    this.polygonDragEnd.emit(point);
  }

  // Cuando se pincha sobre una círculo
  onCircleClick(circle: MapCircle) {
    this.circleClick.emit(circle);
  }

  // Cuando se arrastra y deja caer un círculo
  onCircleDragEnd(circle: MapCircle) {
    this.circleDragEnd.emit(circle);
  }

  // Cuando se modifica el radio de un círculo
  onCircleRadiusChanged(circle: MapCircle) {
    this.circleRadiusChanged.emit(circle);
  }

  // Cuando se realiza una búsqueda en el mapa
  onMapSearch(search: MapSearch) {
    this.mapSearch.emit(search);
  }

  // Establece el centro del mapa
  setCenter(position: MapLatLng) {
    this.map.setCenter(position);
  }

  // Establece el nivel de zoom
  setZoom(zoom: number) {
    this.map.setZoom(zoom);
    this.zoom = zoom;
  }

  resize() {
    this.map.resize();
  }

  // Añade un marcador al mapa
  addMarker(markerData: any): MapMarker {
    // Creo el marcador
    const marker = this.map.addMarker(
      this.markerId,
      markerData.title !== undefined ? markerData.title : '',
      markerData.label !== undefined ? markerData.label : '',
      markerData.content !== undefined ? markerData.content : '',
      markerData.position !== undefined ? markerData.position : new MapLatLng(0, 0),
      markerData.icon !== undefined ? markerData.icon : '',
      markerData.zIndex !== undefined ? markerData.zIndex : 0,
      markerData.drag !== undefined ? markerData.drag : false,
      markerData.visible !== undefined ? markerData.visible : true,
      markerData.minZoom !== undefined ? markerData.minZoom : 0,
      markerData.maxZoom !== undefined ? markerData.maxZoom : 22,
      false,
      false
    );
    // Añado los datos de negocio
    marker.setData(markerData.dataModel);
    // Añado el marcador a la lista
    this.markersList.set(this.markerId++, marker);
    return marker;
  }

  // Borra un marcador
  removeMarker(marker: MapMarker) {
    this.overlapMarkers.restoreMarkersPos();
    this.markersList.delete(marker.id);
    this.map.removeMarker(marker);
  }

  // Borra todos los marcadores
  removeMarkers() {
    this.overlapMarkers.restoreMarkersPos();
    for (const [key, marker] of this.markersList) {
      this.map.removeMarker(marker);
    }
    this.markersList.clear();
  }

  // Crea un cluster
  addCluster(minZoom: number): MapCluster {
    const cluster = this.map.addCluster(this.clusterId, minZoom);
    // Añado el cluster a la lista
    this.clusterList.set(this.clusterId++, cluster);
    return cluster;
  }

  // Borra un cluster
  removeCluster(cluster: any) {
    this.clusterList.delete(cluster.id);
    this.map.removeCluster(cluster);
  }

  // Borra todos los cluster
  removeClusters() {
    for (const [key, cluster] of this.clusterList) {
      this.map.removeCluster(cluster);
    }
    this.clusterList.clear();
  }

  // Añade un marcador al cluster
  addMarkerCluster(cluster: MapCluster, markerData: any): MapMarker {
    // Creo el marcador
    const marker = this.map.addMarker(
      this.markerId,
      markerData.title !== undefined ? markerData.title : '',
      markerData.label !== undefined ? markerData.label : '',
      markerData.content !== undefined ? markerData.content : '',
      markerData.position !== undefined ? markerData.position : new MapLatLng(0, 0),
      markerData.icon !== undefined ? markerData.icon : '',
      markerData.zIndex !== undefined ? markerData.zIndex : 0,
      markerData.drag !== undefined ? markerData.drag : false,
      markerData.visible !== undefined ? markerData.visible : true,
      cluster.maxZoom,
      22,
      true,
      false
    );
    // Añado los datos de negocio
    marker.setData(markerData.dataModel);
    // Añado el marcador a la lista
    this.markersList.set(this.markerId++, marker);
    cluster.addMarker(marker);
    return marker;
  }

  // Repinta un cluster de marcadores
  repaintCluster(cluster: any) {
    this.clusterList.get(cluster.id)?.repaint();
  }

  // Devuelve las coordenadas de las equinas del mapa
  getBounds(): MapBounds {
    return this.map.getBounds();
  }

  // Permite hacer un zoom de encuadre
  fitTo(mapBounds: MapBounds) {
    this.map.fitTo(mapBounds);
  }

  // Permite centrar el mapa en un punto con animación
  panTo(point: MapLatLng) {
    this.map.panTo(point);
  }

  // Añade una polilinea al mapa
  addPolyline(polylineData: any): MapPolyline {
    // Creo la polilinea
    const polyline = this.map.addPolyline(
      this.polylineId,
      polylineData.color !== undefined ? polylineData.color : '#FFFFFF',
      polylineData.weight !== undefined ? polylineData.weight : 1,
      polylineData.opacity !== undefined ? polylineData.opacity : 1,
      polylineData.clickable !== undefined ? polylineData.clickable : false
    );
    // Añado la pololinea a la lista
    this.polylineList.set(this.polylineId++, polyline);
    return polyline;
  }

  // Añade un punto a una polilinea
  addPolylinePoint(polyline: MapPolyline, pointData: any): MapPolylinePoint {
    // Creo el punto
    const point = this.map.addPolylinePoint(
      this.polylinePointId++,
      polyline,
      pointData.content !== undefined ? pointData.content : '',
      pointData.position !== undefined ? pointData.position : new MapLatLng(0, 0)
    );
    // Añado los datos de negocio
    point.setData(pointData.dataModel);
    return point;
  }

  // Borra una polilinea
  removePolyline(polyline: MapPolyline) {
    this.polylineList.delete(polyline.id);
    this.map.removePolyline(polyline);
  }

  // Borra todas las polilineas
  removePolylines() {
    for (const [key, polyline] of this.polylineList) {
      this.map.removePolyline(polyline);
    }
    this.polylineList.clear();
  }

  // Añade un polígono al mapa
  addPolygon(polygonData: any): MapPolygon {
    // Creo un polçigono
    const polygon = this.map.addPolygon(
      this.polygonId,
      polygonData.strokeColor !== undefined ? polygonData.strokeColor : '#FFFFFF',
      polygonData.strokeWeight !== undefined ? polygonData.strokeWeight : 1,
      polygonData.strokeOpacity !== undefined ? polygonData.strokeOpacity : 1,
      polygonData.fillColor !== undefined ? polygonData.fillColor : '#FFFFFF',
      polygonData.fillOpacity !== undefined ? polygonData.fillOpacity : 1,
      polygonData.editable !== undefined ? polygonData.editable : false,
      polygonData.hole !== undefined ? polygonData.hole : false,
      polygonData.content !== undefined ? polygonData.content : ''
    );
    // Añado el polígono a la lista
    this.polygonList.set(this.polygonId++, polygon);
    return polygon;
  }

  // Añade un punto a un polígono
  addPolygonPoint(polygon: MapPolygon, pointData: any): MapPolygonPoint {
    // Creo el punto
    const point = this.map.addPolygonPoint(
      this.polygonPointId++,
      polygon,
      pointData.content !== undefined ? pointData.content : '',
      pointData.position !== undefined ? pointData.position : new MapLatLng(0, 0),
      pointData.dataModel !== undefined ? pointData.dataModel : null
    );
    return point;
  }

  // Borra un polígono
  removePolygon(polygon: MapPolygon) {
    this.polygonList.delete(polygon.id);
    this.map.removePolygon(polygon);
  }

  // Borra todos los polígonos
  removePolygons() {
    for (const [key, polygon] of this.polygonList) {
      this.map.removePolygon(polygon);
    }
    this.polygonList.clear();
  }

  // Añade un círculo al mapa
  addCircle(circleData: any): MapCircle {
    // Creo el círculo
    const circle = this.map.addCircle(
      this.circleId,
      circleData.content !== undefined ? circleData.content : '',
      circleData.strokeColor !== undefined ? circleData.strokeColor : '#FFFFFF',
      circleData.strokeOpacity !== undefined ? circleData.strokeOpacity : 1,
      circleData.strokeWeight !== undefined ? circleData.strokeWeight : 1,
      circleData.fillColor !== undefined ? circleData.fillColor : '#FFFFFF',
      circleData.fillOpacity !== undefined ? circleData.fillOpacity : 1,
      circleData.position !== undefined ? circleData.position : new MapLatLng(0, 0),
      circleData.radius !== undefined ? circleData.radius : 50,
      circleData.editable !== undefined ? circleData.editable : false,
      circleData.draggable !== undefined ? circleData.draggable : false
    );
    // Añado los datos de negocio
    circle.setData(circleData.dataModel);
    // Añado la pololinea a la lista
    this.circleList.set(this.circleId++, circle);
    return circle;
  }

  // Borra un círculo
  removeCircle(circle: MapCircle) {
    this.circleList.delete(circle.id);
    this.map.removeCircle(circle);
  }

  // Borra todos los círculos
  removeCircles() {
    for (const [key, circle] of this.circleList) {
      this.map.removeCircle(circle);
    }
    this.circleList.clear();
  }

  // Añade un mapa de calor al mapa
  addHeatMap(data: any): HeatMap {
    // Creo el mapa de calor
    const heatMap = this.map.addHeatMap(
      this.heatMapId,
      (data !== undefined && data.points !== undefined) ? data.points : null,
      (data !== undefined && data.markers !== undefined) ? data.markers : null,
      (data !== undefined && data.radius !== undefined) ? data.radius : 20,
      (data !== undefined && data.dissipating !== undefined) ? data.dissipating : true,
      (data !== undefined && data.maxIntensity !== undefined) ? data.maxIntensity : 0,
      (data !== undefined && data.opacity !== undefined) ? data.opacity : 0.6,
      (data !== undefined && data.gradient !== undefined) ? data.gradient : null
    );
    this.heatMapList.set(this.heatMapId++, heatMap);
    return heatMap;
  }

  // Borra un mapa de calor
  removeHeatMap(heatMap: HeatMap) {
    this.heatMapList.delete(heatMap.id);
    this.map.removeHeatMap(heatMap);
  }

  // Permite subscribirse para recibir cuando se ha cargado el mapa
  public subscribeOnMapReady(objInstance: any, subscriberFn: any): Subscription {
    return this.mapReady.subscribe(map => {
      subscriberFn(objInstance, map);
    });
  }

  // Permite subscribirse para recibir cuando se pincha sobre la cartografía
  public subscribeOnMapClick(objInstance: any, subscriberFn: any): Subscription {
    return this.mapClick.subscribe(position => {
      subscriberFn(objInstance, position);
    });
  }

  // Permite subscribirse para recibir cuando se mueve el cursor sobre el mapa
  public subscribeOnMapMouseMove(objInstance: any, subscriberFn: any): Subscription {
    return this.mapMouseMove.subscribe(position => {
      subscriberFn(objInstance, position);
    });
  }

  // Permite subscribirse para recibir cuando cambia el encuadre del mapa
  public subscribeOnBoundsChange(objInstance: any, subscriberFn: any): Subscription {
    return this.boundsChange.subscribe(bounds => {
      subscriberFn(objInstance, bounds);
    });
  }

  // Permite subscribirse para recibir cuando cambia el zoom del mapa
  public subscribeOnZoomChange(objInstance: any, subscriberFn: any): Subscription {
    return this.zoomChange.subscribe(zoom => {
      subscriberFn(objInstance, zoom);
    });
  }

  // Permite subscribirse para recibir cuando se pincha sobre un marcador
  public subscribeOnMarkerClick(objInstance: any, subscriberFn: any): Subscription {
    return this.markerClick.subscribe(marker => {
      subscriberFn(objInstance, marker);
    });
  }

  // Permite subscribirse para recibir cuando se deja caer un marcador despues de haberlo movido
  public subscribeOnMarkerDragEnd(objInstance: any, subscriberFn: any): Subscription {
    return this.markerDragEnd.subscribe(marker => {
      subscriberFn(objInstance, marker);
    });
  }

  // Permite subscribirse para recibir cuando se mueve un marcador
  public subscribeOnMarkerMove(objInstance: any, subscriberFn: any): Subscription {
    return this.markerMove.subscribe(marker => {
      subscriberFn(objInstance, marker);
    });
  }

  // Permite subscribirse para recibir cuando se deja caer un círculo despues de haberlo movido
  public subscribeOnCircleDragEnd(objInstance: any, subscriberFn: any): Subscription {
    return this.circleDragEnd.subscribe(circle => {
      subscriberFn(objInstance, circle);
    });
  }

  // Permite subscribirse para recibir cuando se pincha sobre un círculo
  public subscribeOnCircleClick(objInstance: any, subscriberFn: any): Subscription {
    return this.circleClick.subscribe(circle => {
      subscriberFn(objInstance, circle);
    });
  }

  // Permite subscribirse para recibir cuando se modifica el radio de un círculo
  public subscribeOnCircleRadiusChange(objInstance: any, subscriberFn: any): Subscription {
    return this.circleRadiusChanged.subscribe(circle => {
      subscriberFn(objInstance, circle);
    });
  }

  // Permite subscribirse para recibir cuando se pincha sobre una polilinea
  public subscribeOnPolylineClick(objInstance: any, subscriberFn: any): Subscription {
    return this.polylineClick.subscribe(point => {
      subscriberFn(objInstance, point);
    });
  }

  // Permite subscribirse para recibir cuando se pincha sobre un polígono
  public subscribeOnPolygonClick(objInstance: any, subscriberFn: any): Subscription {
    return this.polygonClick.subscribe(point => {
      subscriberFn(objInstance, point);
    });
  }
  // Dejo este método por retrocompatilbilidad
  public subscribeOnPoligonClick(objInstance: any, subscriberFn: any): Subscription {
    return this.polygonClick.subscribe(point => {
      subscriberFn(objInstance, point);
    });
  }

  // Permite subscribirse para recibir cuando se mueve un punto de un polígono
  public subscribeOnPolygonDragEnd(objInstance: any, subscriberFn: any): Subscription {
    return this.polygonDragEnd.subscribe(point => {
      subscriberFn(objInstance, point);
    });
  }

  // Permite subscribirse para recibir cuando se realiza una búsqueda en el mapa
  public subscribeOnMapSearch(objInstance: any, subscriberFn: any): Subscription {
    return this.mapSearch.subscribe((search: MapSearch) => {
      subscriberFn(objInstance, search);
    });
  }

  // Permite cargar un archivo GeoJson desde disco
  public loadGeoJson(file: string, options: any = null): any {
    return this.map.loadGeoJson(file, options);
  }

  // Permite cargar un archivo GeoJson
  public addGeoJson(file: any, options: any = null): any {
    return this.map.addGeoJson(file, options);
  }

  // Permite borrar un GeoJson previamente cargado
  public removeGeoJson(file: any): void {
    this.map.removeGeoJson(file);
  }

  // Crea un cluster
  addClusterV2(minZoom: number): MapClusterV2 {
    const cluster = new MapClusterV2(this, minZoom, this.clusterIdV2);
    // Añado el cluster a la lista
    this.clusterListV2.set(this.clusterIdV2++, cluster);
    return cluster;
  }

  // Borra un cluster
  removeClusterV2(cluster: MapClusterV2) {
    this.clusterListV2.delete(cluster.id);
    cluster.removeCluster();
  }

  // Borra todos los cluster
  removeClustersV2() {
    for (const [key, cluster] of this.clusterListV2) {
      cluster.removeCluster();
    }
    this.clusterListV2.clear();
  }

  // Modifica el nivel de zoom
  setZoomClusterV2(cluster: MapClusterV2, zoom: number) {
    cluster.setZoom(zoom);
  }

  // Añade un marcador al cluster
  addMarkerClusterV2(cluster: MapClusterV2, markerData: any, refresh = true): MapMarker {
    // Creo el marcador
    const marker = this.map.addMarker(
      this.markerId,
      markerData.title !== undefined ? markerData.title : '',
      markerData.label !== undefined ? markerData.label : '',
      markerData.content !== undefined ? markerData.content : '',
      markerData.position !== undefined ? markerData.position : new MapLatLng(0, 0),
      markerData.icon !== undefined ? markerData.icon : '',
      markerData.zIndex !== undefined ? markerData.zIndex : 0,
      markerData.drag !== undefined ? markerData.drag : false,
      markerData.visible !== undefined ? markerData.visible : true,
      1,
      22,
      false,
      cluster
    );
    // Añado los datos de negocio
    marker.dataModel = markerData.dataModel !== undefined ? markerData.dataModel : null;
    // Añado el marcador a la lista
    this.markersList.set(this.markerId++, marker);
    cluster.addMarker(marker, refresh);
    return marker;
  }

  // Repinta un cluster de marcadores
  repaintClusterV2(cluster: MapClusterV2) {
    this.clusterListV2.get(cluster.id)?.refresh(true);
  }

  // Permite cambiar el puntero del ratón sobre el mapa
  setMousePointer(pointer: string) {
    this.map.setMousePointer(pointer);
  }

  // Devuelve pa proyección del mapa
  getProjection(): any {
    return this.map.getProjection();
  }

  // Pasa una coordenada geográfica a un punto
  fromLatlngToPoint(position: MapLatLng): MapPoint {
    return this.map.fromLatlngToPoint(position);
  }

  // Pasa un punto a corrdenada geográfica
  fromPointToLatlng(point: MapPoint): MapLatLng {
    return this.map.fromPointToLatlng(point);
  }

  // Pasa una coordenada geográfica a un punto de pantalla
  latLngToScreenPoint(position: MapLatLng): MapPoint {
    return this.map.latLngToScreenPoint(position);
  }

  // Pasa un punto de pantalla a coordenada geográfica
  screenPointToLatLng(point: MapPoint): MapLatLng {
    return this.map.screenPointToLatLng(point);
  }

  // Crea un objeto para calcular itinerarios
  itinerary(data: any): MapItinerary {
    return this.map.itinerary(data.fromInput, data.toInput, data.fromPos, data.toPos, data.resultDiv);
  }

  // Permite borrar un itinerario
  removeItineary(itinerary: MapItinerary) {
    if (itinerary) {
      itinerary.remove();
    }
  }

  async loadKml(url: string): Promise<any> {
    return this.map.loadKml(url);
  }

  addKml(xml: string): any {
    return this.map.addKml(xml);
  }

  // Permite borrar un Kml previamente cargado
  public removeKml(file: any): void {
    this.map.removeKml(file);
  }

  // Devuelve la lista de marcadores
  public getMarkerList(): Map<number, MapMarker> {
    return this.markersList;
  }

  // Oculta el infowindows de los marcadores
  public closeInfowindowMarker(): void {
    this.map.closeInfowindowMarker();
  }

  public addPolygonsFromGeoJson(geoJson: any, editable: boolean, options: any = undefined): MapPolygon[] {
    const res: MapPolygon[] = [];
    // Compruebo si es una array de Features o un solo Feature
    if (geoJson instanceof Array) {
      geoJson.forEach((feature: any) => {
        this.addPolygonsFromFeature(feature, editable, options, res);
      });
    } else {
      this.addPolygonsFromFeature(geoJson, editable, options, res);
    }
    return res;
  }

  private addPolygonsFromFeature(feature: any, editable: boolean, options: any, res: MapPolygon[]): MapPolygon[] {
    // Sólo me interesan los polígonos
    if (feature.geometry.type === 'MultiPolygon') {
      // Recorro los multipolígonos
      feature.geometry.coordinates.forEach((polygons: any) => {
        // Recorro los polígonos
        polygons.forEach((polygon: any) => {
          // Creo el nuevo polígono
          const newPolygon = this.addPolygon({
            content: options ? options.content : undefined,
            dataModel: options ? options.dataModel : undefined,
            strokeColor: options && options.strokeColor ? options.strokeColor : '#ff0000',
            strokeOpacity: options && options.strokeOpacity ? options.strokeOpacity : 0.3,
            strokeWeight: options && options.strokeWeight ? options.strokeWeight : 1,
            fillColor: options && options.fillColor ? options.fillColor : '#0000ff',
            fillOpacity: options && options.fillOpacity ? options.fillOpacity : 0.1,
            editable: editable
          });
          res.push(newPolygon);
          // Añado los puntos al nuevo polígono
          polygon.forEach((points: any) => {
            this.addPolygonPoint(newPolygon, {
              dataModel: options ? options.dataModel : undefined,
              content: (options && options.content) ? options.content :
                (feature.properties && feature.properties.name) ? feature.properties.name : '',
              position: new MapLatLng(points[1], points[0])
            });
          });
        });
      });
    }
    return res;
  }

  // Devuelve el nivel máximo de zoom
  getMaxZoom() {
    return this.map.getMaxZoom();
  }


}
